<template>
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div class="min-h-screen bg-gray-50">
        <div class="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <Breadcrumbs
            :breadcrumbs="[
              { name: 'Dashboard', href: route('dashboard') },
              {
                name: `Agenda Kegiatan`,
                href: route('agenda.index'),
              },
              {
                name: `${agendaData?.judul || 'Detail Agenda'}`,
              },
            ]"
          />

          <!-- Header Section -->
          <div class="mb-8">
            <div
              class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <h1 class="text-3xl font-bold text-gray-900">Detail Agenda</h1>
                <p class="mt-2 text-lg text-gray-600">
                  Informasi lengkap agenda kegiatan
                </p>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
            <!-- Information Card -->
            <div class="order-2 lg:order-1">
              <div class="bg-white border border-gray-200 shadow-lg rounded-xl">
                <div
                  class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-xl sm:px-6 sm:py-4"
                >
                  <h2
                    class="flex items-center text-lg font-bold text-white sm:text-xl"
                  >
                    <i class="mr-2 fa-solid fa-calendar-check sm:mr-3"></i>
                    Informasi Agenda
                  </h2>
                </div>
                <div class="p-4 sm:p-6">
                  <dl class="divide-y divide-gray-200">
                    <div class="grid grid-cols-1 gap-4 py-4 sm:grid-cols-3">
                      <dt
                        class="flex items-center text-sm font-semibold text-gray-900"
                      >
                        <i class="mr-2 text-indigo-600 fa-solid fa-heading"></i>
                        Judul
                      </dt>
                      <dd
                        class="text-sm font-medium text-gray-700 sm:col-span-2"
                      >
                        {{ agendaData?.judul || "-" }}
                      </dd>
                    </div>

                    <div class="grid grid-cols-1 gap-4 py-4 sm:grid-cols-3">
                      <dt
                        class="flex items-center text-sm font-semibold text-gray-900"
                      >
                        <i
                          class="mr-2 text-indigo-600 fa-solid fa-align-left"
                        ></i>
                        Deskripsi
                      </dt>
                      <dd class="text-sm text-gray-700 sm:col-span-2">
                        {{ agendaData?.deskripsi || "-" }}
                      </dd>
                    </div>

                    <div class="grid grid-cols-1 gap-4 py-4 sm:grid-cols-3">
                      <dt
                        class="flex items-center text-sm font-semibold text-gray-900"
                      >
                        <i
                          class="mr-2 text-indigo-600 fa-solid fa-calendar-days"
                        ></i>
                        Tanggal
                      </dt>
                      <dd class="text-sm text-gray-700 sm:col-span-2">
                        <div class="flex flex-col space-y-1">
                          <div class="flex items-center">
                            <i
                              class="mr-2 text-green-500 fa-solid fa-calendar-start"
                            ></i>
                            <span class="font-medium">Mulai:</span>
                            <span class="ml-1">{{
                              agendaData?.tanggal_mulai || "-"
                            }}</span>
                          </div>
                          <div class="flex items-center">
                            <i
                              class="mr-2 text-red-500 fa-solid fa-calendar-check"
                            ></i>
                            <span class="font-medium">Selesai:</span>
                            <span class="ml-1">{{
                              agendaData?.tanggal_selesai || "-"
                            }}</span>
                          </div>
                        </div>
                      </dd>
                    </div>

                    <div
                      class="grid grid-cols-1 gap-4 py-4 sm:grid-cols-3"
                      v-if="agendaData?.lokasi"
                    >
                      <dt
                        class="flex items-center text-sm font-semibold text-gray-900"
                      >
                        <i
                          class="mr-2 text-indigo-600 fa-solid fa-map-marker-alt"
                        ></i>
                        Lokasi
                      </dt>
                      <dd class="text-sm text-gray-700 sm:col-span-2">
                        {{ agendaData?.lokasi || "-" }}
                      </dd>
                    </div>

                    <div class="grid grid-cols-1 gap-4 py-4 sm:grid-cols-3">
                      <dt
                        class="flex items-center text-sm font-semibold text-gray-900"
                      >
                        <i
                          class="mr-2 text-indigo-600 fa-solid fa-info-circle"
                        ></i>
                        Status
                      </dt>
                      <dd class="text-sm text-gray-700 sm:col-span-2">
                        <span
                          :class="{
                            'bg-green-100 text-green-800':
                              agendaData?.status === 'selesai',
                            'bg-blue-100 text-blue-800':
                              agendaData?.status === 'terjadwal',
                            'bg-yellow-100 text-yellow-800':
                              agendaData?.status === 'berlangsung',
                            'bg-red-100 text-red-800':
                              agendaData?.status === 'dibatalkan',
                            'bg-gray-100 text-gray-800':
                              agendaData?.status === 'ditunda',
                          }"
                          class="inline-flex items-center px-3 py-1 text-xs font-medium capitalize rounded-full"
                        >
                          {{ agendaData?.status || "-" }}
                        </span>
                      </dd>
                    </div>
                  </dl>
                </div>

                <!-- Action Buttons -->
                <div
                  v-if="$page.props.auth.user"
                  class="px-4 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl sm:px-6"
                >
                  <div
                    class="flex flex-col gap-3 sm:flex-row sm:justify-center"
                  >
                    <button
                      @click="openEditModal"
                      class="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                      title="Edit Dokumen"
                    >
                      <i class="mr-2 fa-solid fa-edit"></i>
                      Edit Dokumen
                    </button>
                    <button
                      @click="openFileModal"
                      class="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                      title="Edit File"
                    >
                      <i class="mr-2 fa-solid fa-file-edit"></i>
                      Edit File
                    </button>
                    <button
                      @click="confirmDelete(agendaData?.idagenda)"
                      class="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-white bg-red-600 border border-transparent rounded-lg shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                      title="Hapus Agenda"
                    >
                      <i class="mr-2 fa-solid fa-trash"></i>
                      Hapus Agenda
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <!-- Image Section -->
            <div class="order-1 lg:order-2 lg:col-span-2">
              <div class="bg-white border border-gray-200 shadow-lg rounded-xl">
                <div
                  class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-xl sm:px-6 sm:py-4"
                >
                  <h2
                    class="flex items-center text-lg font-bold text-white sm:text-xl"
                  >
                    <i class="mr-2 fa-solid fa-image sm:mr-3"></i>
                    Gambar Agenda
                  </h2>
                </div>
                <div class="p-4 sm:p-6">
                  <div
                    v-if="agendaData?.gambar"
                    class="relative overflow-hidden bg-gray-100 border border-gray-200 rounded-lg shadow-sm"
                  >
                    <img
                      :src="agendaData?.gambar"
                      :alt="agendaData?.judul || 'Gambar Agenda'"
                      class="object-cover w-full h-auto max-h-96"
                    />
                  </div>
                  <div
                    v-else
                    class="relative flex flex-col items-center justify-center h-64 border-2 border-gray-200 border-dashed rounded-lg bg-gray-50"
                  >
                    <i
                      class="mb-3 text-4xl text-gray-400 fa-solid fa-image"
                    ></i>
                    <span class="text-sm text-gray-500">Belum ada gambar</span>
                  </div>
                </div>
              </div>

              <!-- Modals -->
              <!-- Modal Edit Agenda -->
              <div
                v-if="isModalOpen"
                class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
              >
                <div
                  class="relative w-full max-w-2xl mx-auto bg-white shadow-xl rounded-xl max-h-[90vh] overflow-y-auto"
                >
                  <div
                    class="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-xl"
                  >
                    <div class="flex items-center justify-between">
                      <h3 class="text-lg font-bold text-white">
                        <i class="mr-2 fa-solid fa-edit"></i>
                        Edit Agenda
                      </h3>
                      <button
                        @click="isModalOpen = false"
                        class="text-white rounded-lg hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-600"
                      >
                        <svg
                          class="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M6 18L18 6M6 6l12 12"
                          ></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <form @submit.prevent="submitEdit">
                    <div class="p-6 space-y-6">
                      <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                          <label
                            class="block mb-2 text-sm font-semibold text-gray-700"
                          >
                            <i class="mr-2 fa-solid fa-heading"></i>
                            Judul
                          </label>
                          <input
                            type="text"
                            v-model="editForm.judul"
                            class="w-full px-4 py-3 transition-colors duration-200 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="Masukkan judul agenda"
                            required
                          />
                        </div>
                        <div>
                          <label
                            class="block mb-2 text-sm font-semibold text-gray-700"
                          >
                            <i class="mr-2 fa-solid fa-info-circle"></i>
                            Status
                          </label>
                          <select
                            v-model="editForm.status"
                            class="w-full px-4 py-3 transition-colors duration-200 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          >
                            <option value="">Pilih Status</option>
                            <option value="terjadwal">Terjadwal</option>
                            <option value="berlangsung">Berlangsung</option>
                            <option value="selesai">Selesai</option>
                            <option value="dibatalkan">Dibatalkan</option>
                            <option value="ditunda">Ditunda</option>
                          </select>
                        </div>
                        <div>
                          <label
                            class="block mb-2 text-sm font-semibold text-gray-700"
                          >
                            <i class="mr-2 fa-solid fa-calendar-start"></i>
                            Tanggal Mulai
                          </label>
                          <input
                            type="date"
                            v-model="editForm.tanggal_mulai"
                            class="w-full px-4 py-3 transition-colors duration-200 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                        <div>
                          <label
                            class="block mb-2 text-sm font-semibold text-gray-700"
                          >
                            <i class="mr-2 fa-solid fa-calendar-check"></i>
                            Tanggal Selesai
                          </label>
                          <input
                            type="date"
                            v-model="editForm.tanggal_selesai"
                            class="w-full px-4 py-3 transition-colors duration-200 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label
                          class="block mb-2 text-sm font-semibold text-gray-700"
                        >
                          <i class="mr-2 fa-solid fa-map-marker-alt"></i>
                          Lokasi
                        </label>
                        <input
                          type="text"
                          v-model="editForm.lokasi"
                          class="w-full px-4 py-3 transition-colors duration-200 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Masukkan lokasi agenda"
                        />
                      </div>
                      <div>
                        <label
                          class="block mb-2 text-sm font-semibold text-gray-700"
                        >
                          <i class="mr-2 fa-solid fa-align-left"></i>
                          Deskripsi
                        </label>
                        <textarea
                          v-model="editForm.deskripsi"
                          rows="4"
                          class="w-full px-4 py-3 transition-colors duration-200 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Masukkan deskripsi agenda"
                        ></textarea>
                      </div>
                    </div>
                    <div
                      class="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl"
                    >
                      <div class="flex justify-end space-x-3">
                        <button
                          type="button"
                          @click="isModalOpen = false"
                          class="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                        >
                          <i class="mr-2 fa-solid fa-times"></i>
                          Batal
                        </button>
                        <button
                          type="submit"
                          class="px-6 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                        >
                          <i class="mr-2 fa-solid fa-save"></i>
                          Simpan Perubahan
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              <!-- Modal Edit File -->
              <div
                v-if="isModalFile"
                class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
              >
                <div
                  class="relative w-full max-w-md mx-auto bg-white shadow-xl rounded-xl"
                >
                  <div
                    class="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-xl"
                  >
                    <div class="flex items-center justify-between">
                      <h3 class="text-lg font-bold text-white">
                        <i class="mr-2 fa-solid fa-image"></i>
                        Edit File Agenda
                      </h3>
                      <button
                        @click="isModalFile = false"
                        class="text-white rounded-lg hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-600"
                      >
                        <svg
                          class="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M6 18L18 6M6 6l12 12"
                          ></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <form @submit.prevent="submitFile">
                    <div class="p-6">
                      <div class="space-y-4">
                        <div>
                          <label
                            class="block mb-2 text-sm font-semibold text-gray-700"
                          >
                            <i class="mr-2 fa-solid fa-upload"></i>
                            Pilih Gambar
                          </label>
                          <input
                            type="file"
                            @change="onFileChange"
                            accept="image/*"
                            class="w-full px-4 py-3 transition-colors duration-200 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                            required
                          />
                          <p class="mt-2 text-xs text-gray-500">
                            Format yang didukung: JPG, JPEG, PNG, GIF. Maksimal
                            2MB.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      class="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl"
                    >
                      <div class="flex justify-end space-x-3">
                        <button
                          type="button"
                          @click="isModalFile = false"
                          class="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                        >
                          <i class="mr-2 fa-solid fa-times"></i>
                          Batal
                        </button>
                        <button
                          type="submit"
                          class="px-6 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                        >
                          <i class="mr-2 fa-solid fa-upload"></i>
                          Upload File
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Container>
</template>

<script setup>
import { defineProps, watch, ref, computed } from "vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import Container from "@/Layouts/Container.vue";
import Header from "@/Components/Header.vue";
import Swal from "sweetalert2";
import { router, useForm } from "@inertiajs/vue3";

const props = defineProps({
  agenda: {
    type: Object,
    required: true,
  },
});

// Computed property to safely access agenda data
const agendaData = computed(() => {
  if (!props.agenda) return null;
  // Handle both direct object and nested data structure
  return props.agenda.data || props.agenda;
});

const isModalOpen = ref(false);
const isModalFile = ref(false);

// Form objects for editing using Inertia forms
const editForm = useForm({
  judul: "",
  deskripsi: "",
  tanggal_mulai: "",
  tanggal_selesai: "",
  lokasi: "",
  status: "",
});

const fileForm = useForm({
  gambar: null,
});

// Modal control functions
function openEditModal() {
  if (!agendaData.value) {
    Swal.fire({
      title: "Error!",
      text: "Data agenda tidak tersedia.",
      icon: "error",
      confirmButtonText: "OK",
    });
    return;
  }

  // Populate form with current data
  editForm.judul = agendaData.value.judul || "";
  editForm.deskripsi = agendaData.value.deskripsi || "";
  editForm.tanggal_mulai = agendaData.value.tanggal_mulai || "";
  editForm.tanggal_selesai = agendaData.value.tanggal_selesai || "";
  editForm.lokasi = agendaData.value.lokasi || "";
  editForm.status = agendaData.value.status || "";

  isModalOpen.value = true;
}

function openFileModal() {
  if (!agendaData.value) {
    Swal.fire({
      title: "Error!",
      text: "Data agenda tidak tersedia.",
      icon: "error",
      confirmButtonText: "OK",
    });
    return;
  }

  // Reset file form
  fileForm.reset();

  isModalFile.value = true;
}

// Submit functions
function submitEdit() {
  // Debug: Check if we have the agenda data
  if (!agendaData.value || !agendaData.value.idagenda) {
    Swal.fire({
      title: "Error!",
      text: "ID agenda tidak ditemukan.",
      icon: "error",
      confirmButtonText: "OK",
    });
    return;
  }

  console.log("Submitting edit for agenda:", agendaData.value.idagenda);
  console.log("Form data:", editForm.data());

  editForm.put(route("agenda.update", agendaData.value.idagenda), {
    onSuccess: () => {
      isModalOpen.value = false;
      Swal.fire({
        title: "Berhasil!",
        text: "Agenda berhasil diperbarui.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
      // Refresh page to show updated data
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    },
    onError: (errors) => {
      console.error("Update errors:", errors);
      Swal.fire({
        title: "Gagal!",
        text: "Terjadi kesalahan saat memperbarui data.",
        icon: "error",
        confirmButtonText: "OK",
      });
    },
    preserveScroll: true,
  });
}

function submitFile() {
  if (!fileForm.gambar) {
    Swal.fire({
      title: "Peringatan!",
      text: "Silakan pilih file gambar terlebih dahulu.",
      icon: "warning",
      confirmButtonText: "OK",
    });
    return;
  }

  fileForm.post(route("agenda.upload", agendaData.value.idagenda), {
    forceFormData: true,
    onSuccess: () => {
      isModalFile.value = false;
      fileForm.reset();
      Swal.fire({
        title: "Berhasil!",
        text: "File agenda berhasil diperbarui.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
      // Refresh page to show updated data
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    },
    onError: (errors) => {
      console.error("File upload errors:", errors);
      Swal.fire({
        title: "Gagal!",
        text: errors.gambar || "Terjadi kesalahan saat mengupload file.",
        icon: "error",
        confirmButtonText: "OK",
      });
    },
    preserveScroll: true,
  });
}

// File handling
function onFileChange(e) {
  fileForm.gambar = e.target.files[0];
}
function confirmDelete(idagenda) {
  Swal.fire({
    title: "Apakah Anda yakin?",
    text: "Agenda ini akan dihapus secara permanen!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#dc2626",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Ya, hapus!",
    cancelButtonText: "Batal",
  }).then((result) => {
    if (result.isConfirmed) {
      router.delete(route("agenda.destroy", idagenda), {
        onSuccess: () => {
          Swal.fire({
            title: "Berhasil!",
            text: "Agenda berhasil dihapus.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          }).then(() => {
            // Redirect to agenda index after successful deletion
            router.visit(route("agenda.index"));
          });
        },
        onError: (errors) => {
          console.error("Delete error:", errors);
          Swal.fire({
            title: "Gagal!",
            text: "Terjadi kesalahan saat menghapus agenda.",
            icon: "error",
            confirmButtonText: "OK",
          });
        },
      });
    }
  });
}
</script>
